// (c) chaehongee. Confidential and proprietary.

import { Suspense, useEffect, useMemo, useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { useGLTF, useTexture } from '@react-three/drei'
import {
  Box3,
  DoubleSide,
  Euler,
  Group,
  MathUtils,
  type Mesh,
  type MeshBasicMaterial,
  Quaternion,
  SRGBColorSpace,
  Vector3,
  type Texture,
} from 'three'
import { SPHERE_CONFIG, HERO_CONFIG } from './constants'

type HeroGLBSceneProps = {
  src: string
}

export function HeroGLBScene(props: HeroGLBSceneProps) {
  const { src } = props
  const pointerRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const w = window.innerWidth || 1
      const h = window.innerHeight || 1
      const nx = (e.clientX / w - 0.5) * 2
      const ny = (e.clientY / h - 0.5) * 2
      pointerRef.current.x = clampSigned1(nx)
      pointerRef.current.y = clampSigned1(ny)
    }

    const onLeave = () => {
      pointerRef.current.x = 0
      pointerRef.current.y = 0
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('blur', onLeave)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('blur', onLeave)
    }
  }, [])

  return (
    <Canvas
      dpr={[1, 2]}
      camera={{ position: [0, 0, 4.2], fov: 34, near: 0.1, far: 100 }}
      shadows
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
    >
      <Suspense fallback={null}>
        <HeroStage />
        <HeroModel src={src} pointerRef={pointerRef} />
      </Suspense>
    </Canvas>
  )
}

export function FooterGLBScene(props: HeroGLBSceneProps) {
  const { src } = props
  const pointerRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const w = window.innerWidth || 1
      const h = window.innerHeight || 1
      const nx = (e.clientX / w - 0.5) * 2
      const ny = (e.clientY / h - 0.5) * 2
      pointerRef.current.x = clampSigned1(nx)
      pointerRef.current.y = clampSigned1(ny)
    }

    const onLeave = () => {
      pointerRef.current.x = 0
      pointerRef.current.y = 0
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('blur', onLeave)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('blur', onLeave)
    }
  }, [])

  return (
    <Canvas
      dpr={[1, 2]}
      camera={{ position: [0, 0, 4.2], fov: 34, near: 0.1, far: 100 }}
      shadows
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
    >
      <Suspense fallback={null}>
        <HeroStage />
        <FooterModel src={src} pointerRef={pointerRef} />
      </Suspense>
    </Canvas>
  )
}

function HeroStage() {
  const { gl, scene } = useThree()

  useEffect(() => {
    gl.setClearColor('#ffffff', 1)
    scene.fog = null
  }, [gl, scene])

  return (
    <>
      <ambientLight intensity={0.3} />
      <directionalLight
        castShadow
        position={[3.5, 4.5, 3.5]}
        intensity={1.0}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-bias={-0.00025}
        shadow-normalBias={0.025}
        shadow-radius={100}
      />
    </>
  )
}

function HeroModel({
  src,
  pointerRef,
}: {
  src: string
  pointerRef: React.MutableRefObject<{ x: number; y: number }>
}) {
  const groupRef = useRef<Group | null>(null)
  const modelRef = useRef<Group | null>(null)

  // drei types use `any` internally; keep surface typed.
  const gltf = useGLTF(src) as unknown as { scene: Group }
  // Clone the scene so we can have multiple instances of the same model
  const scene = useMemo(() => gltf.scene.clone(), [gltf.scene])

  useEffect(() => {
    if (!modelRef.current) return

    // Very subtle depth cue: allow the hero model to cast a soft shadow.
    setCastShadowRecursive(scene, true)

    // Center and scale model to a consistent hero footprint.
    const box = new Box3().setFromObject(modelRef.current)
    const size = new Vector3()
    const center = new Vector3()
    box.getSize(size)
    box.getCenter(center)

    modelRef.current.position.sub(center)

    const maxAxis = Math.max(size.x, size.y, size.z)
    if (maxAxis > 0) {
      const targetSize = 2.4
      const s = targetSize / maxAxis
      modelRef.current.scale.setScalar(s)
    }

    // Slightly lift so the silhouette feels centered.
    modelRef.current.position.y -= 0.05
  }, [scene])

  useFrame((_, delta) => {
    const g = groupRef.current
    if (!g) return

    // Pointer is normalized [-1..1]. Keep motion subtle & premium.
    const px = pointerRef.current.x
    const py = pointerRef.current.y

    const targetRotY = px * 0.35
    const targetRotX = py * 0.18

    g.rotation.y = MathUtils.damp(g.rotation.y, targetRotY, 8, delta)
    g.rotation.x = MathUtils.damp(g.rotation.x, targetRotX, 8, delta)

    const targetPosX = px * 0.25
    const targetPosY = py * 0.16
    g.position.x = MathUtils.damp(g.position.x, targetPosX, 7, delta)
    g.position.y = MathUtils.damp(g.position.y, targetPosY, 7, delta)

    g.rotation.z = MathUtils.damp(g.rotation.z, px * -0.08, 8, delta)
  })

  return (
    <group ref={groupRef}>
      <group ref={modelRef}>
        <primitive object={scene} />
      </group>
      {/* Soft contact shadow for depth. */}
      <mesh
        receiveShadow
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -1.32, 0]}
      >
        <planeGeometry args={[10, 10]} />
        {/* three types aren't exported from R3F for this material; it's fine as JSX */}
        <shadowMaterial transparent opacity={0.1} color="#000000" />
      </mesh>
    </group>
  )
}

function FooterModel({
  src,
  pointerRef,
}: {
  src: string
  pointerRef: React.MutableRefObject<{ x: number; y: number }>
}) {
  const groupRef = useRef<Group | null>(null)
  const modelRef = useRef<Group | null>(null)

  const gltf = useGLTF(src) as unknown as { scene: Group }
  // Clone here as well
  const scene = useMemo(() => gltf.scene.clone(), [gltf.scene])

  useEffect(() => {
    if (!modelRef.current) return

    setCastShadowRecursive(scene, true)

    const box = new Box3().setFromObject(modelRef.current)
    const size = new Vector3()
    const center = new Vector3()
    box.getSize(size)
    box.getCenter(center)

    modelRef.current.position.sub(center)

    const maxAxis = Math.max(size.x, size.y, size.z)
    if (maxAxis > 0) {
      // Slightly smaller for footer
      const targetSize = 2.0
      const s = targetSize / maxAxis
      modelRef.current.scale.setScalar(s)
    }
  }, [scene])

  useFrame((state, delta) => {
    const g = groupRef.current
    if (!g) return

    const t = state.clock.getElapsedTime()
    const px = pointerRef.current.x
    const py = pointerRef.current.y

    const targetRotY = px * 0.35 + Math.sin(t * 0.5) * 0.1
    const targetRotX = py * 0.18 + Math.cos(t * 0.4) * 0.05

    g.rotation.y = MathUtils.damp(g.rotation.y, targetRotY, 8, delta)
    g.rotation.x = MathUtils.damp(g.rotation.x, targetRotX, 8, delta)

    // Bobbing animation
    const floatY = Math.sin(t * 1.5) * 0.15
    const targetPosX = px * 0.25
    const targetPosY = py * 0.16 + floatY

    g.position.x = MathUtils.damp(g.position.x, targetPosX, 7, delta)
    g.position.y = MathUtils.damp(g.position.y, targetPosY, 7, delta)
    
    // Slow continuous rotation
    g.rotation.z = MathUtils.damp(g.rotation.z, px * -0.08 + Math.sin(t * 0.3) * 0.05, 8, delta)
  })

  return (
    <group ref={groupRef}>
      <group ref={modelRef}>
        <primitive object={scene} />
      </group>
    </group>
  )
}

useGLTF.preload('/aetherstudio.glb')

function clampSigned1(v: number): number {
  if (v < -1) return -1
  if (v > 1) return 1
  return v
}

function setCastShadowRecursive(root: Group, castShadow: boolean): void {
  root.traverse((obj) => {
    // `isMesh` is the most reliable discriminator in three.
    if ((obj as Mesh).isMesh) {
      ;(obj as Mesh).castShadow = castShadow
    }
  })
}

type ModelsParallaxSceneProps = {
  imageUrls: string[]
  scrollProgressRef: React.MutableRefObject<number>
  heroAnchorRef?: React.MutableRefObject<{ x: number; y: number }>
}

export function ModelsParallaxScene(props: ModelsParallaxSceneProps) {
  const { imageUrls, scrollProgressRef, heroAnchorRef } = props

  return (
    <Canvas
      dpr={[1, 2]}
      camera={{ position: [0, 0, 7.5], fov: 38, near: 0.1, far: 100 }}
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
    >
      <Suspense fallback={null}>
        <ModelsStage />
        <ModelsSwarm
          imageUrls={imageUrls}
          scrollProgressRef={scrollProgressRef}
          heroAnchorRef={heroAnchorRef}
        />
      </Suspense>
    </Canvas>
  )
}

function ModelsStage() {
  const { gl, scene } = useThree()

  useEffect(() => {
    gl.setClearColor('#ffffff', 1)
    scene.fog = null
  }, [gl, scene])

  return (
    <>
      <ambientLight intensity={0.8} />
      <directionalLight position={[5, 6, 4]} intensity={0.7} />
      <directionalLight position={[-6, 2, 4]} intensity={0.35} />
    </>
  )
}

type SwarmItem = {
  url: string
  colX: number
  colY: number
  colZ: number
  sphereX: number
  sphereY: number
  sphereZ: number
  sphereQx: number
  sphereQy: number
  sphereQz: number
  sphereQw: number
  sphereDepth01: number
  w: number
  h: number
  phase: number
  drift: number
  seed: number
  seed2: number
  seed3: number
}

function ModelsSwarm({
  imageUrls,
  scrollProgressRef,
  heroAnchorRef,
}: {
  imageUrls: string[]
  scrollProgressRef: React.MutableRefObject<number>
  heroAnchorRef?: React.MutableRefObject<{ x: number; y: number }>
}) {
  const { viewport } = useThree()
  // Reduce initial load: start with fewer items, load more as user scrolls
  const MAX_TEXTURES = 30
  const TOTAL_ITEMS = 100
  const textures = useTexture(imageUrls.slice(0, MAX_TEXTURES)) as unknown as Texture[]
  const SPHERE_CENTER_Z = -3.25
  const sphereFrontDirection = useMemo(() => new Vector3(0, 0, 1), [])
  const heroRightWorldPos = useMemo(() => new Vector3(), [])

  useEffect(() => {
    for (const t of textures) {
      t.colorSpace = SRGBColorSpace
      t.anisotropy = 8
      t.needsUpdate = true
    }
  }, [textures])

  const items = useMemo<SwarmItem[]>(() => {
    const availableUrls = imageUrls.slice(0, MAX_TEXTURES)
    if (availableUrls.length === 0) return []

    const urls = Array.from({ length: TOTAL_ITEMS }).map((_, i) => {
      return availableUrls[i % availableUrls.length]
    })
    const columns = 10
    const rows = Math.max(1, Math.ceil(urls.length / columns))

    // Target layout: a line of columns across width, packed to fit height.
    const colSpan = viewport.width * 0.92
    const colStep = colSpan / columns

    const rowSpan = viewport.height * 0.92
    const rowStep = rowSpan / rows

    // Cards: 3:4 aspect ratio. Fit to both column spacing and row spacing.
    const maxCardWFromCols = colStep * 0.62
    const maxCardHFromRows = rowStep * 0.78
    const cardW = Math.min(maxCardWFromCols, maxCardHFromRows * (3 / 4))
    const cardH = cardW * (4 / 3)

    // Sphere: distribute on a shell, sized to viewport.
    // Tune for "entire sphere in view" like the reference.
    const sphereRadius = Math.min(viewport.width, viewport.height) * 0.48
    // Build a ring-like sphere distribution (lat/long) so the outline reads
    // more like the reference (less "random cloud", more structured sphere).
    const rawSpherePoints = urls.map((url, idx) => {
      // Use idx in hash to ensure distinct positions for repeated URLs
      const seed = hashTo01(url + idx)
      const col = idx % columns
      const row = Math.floor(idx / columns)

      const y = 1 - ((row + 0.5) / rows) * 2
      const r = Math.sqrt(Math.max(0, 1 - y * y))

      // Stagger rows for nicer packing; add small deterministic jitter.
      const baseTheta =
        ((col + (row % 2) * 0.5) / columns) * Math.PI * 2
      const jitter = (seed - 0.5) * (Math.PI * 2 / columns) * 0.35
      const theta = baseTheta + jitter

      return { x: Math.cos(theta) * r, y, z: Math.sin(theta) * r }
    })
    const points = centeredUnitSpherePoints(rawSpherePoints)

    return urls.map((url, idx) => {
      const seed = hashTo01(url + idx)
      const seed2 = hashTo01(url + idx + ':b')
      const seed3 = hashTo01(url + idx + ':c')

      const col = idx % columns
      const row = Math.floor(idx / columns)

      const colX = -colSpan / 2 + colStep * (col + 0.5)
      const colY = rowSpan / 2 - rowStep * (row + 0.5)
      const colZ = -0.2 - seed3 * 0.55

      const p = points[idx]
      const sphereX = p.x * sphereRadius
      const sphereY = p.y * sphereRadius
      // Keep sphere centered at the group's origin; we apply the Z-offset to
      // the group itself so rotation doesn't "orbit" the formation sideways.
      const sphereZ = p.z * sphereRadius

      // Proper math: compute the quaternion that rotates a plane's +Z normal
      // to the sphere normal direction, then apply a deterministic roll
      // *around that normal*.
      const normal = new Vector3(p.x, p.y, p.z).normalize()
      const alignQ = new Quaternion().setFromUnitVectors(sphereFrontDirection, normal)
      const roll = (seed2 - 0.5) * 0.7
      const rollQ = new Quaternion().setFromAxisAngle(normal, roll)
      // Compose: align first, then roll around the resulting normal.
      alignQ.premultiply(rollQ)

      return {
        url,
        colX,
        colY,
        colZ,
        sphereX,
        sphereY,
        sphereZ,
        sphereQx: alignQ.x,
        sphereQy: alignQ.y,
        sphereQz: alignQ.z,
        sphereQw: alignQ.w,
        w: cardW,
        h: cardH,
        phase: seed2 * Math.PI * 2,
        drift: 0.85 + seed3 * 0.75,
        seed,
        seed2,
        seed3,
      }
    }).map((it) => ({
      ...it,
      // Keep for per-frame depth scaling without capturing `sphereRadius` in `useFrame`.
      sphereDepth01: (it.sphereZ / Math.max(1e-6, sphereRadius) + 1) / 2,
    }))
  }, [imageUrls, viewport.height, viewport.width])

  const meshesRef = useRef<Array<Mesh | null>>([])
  const groupRef = useRef<Group | null>(null)

  useFrame((state, delta) => {
    const t = state.clock.getElapsedTime()
    const pRaw = scrollProgressRef.current
    const p = clamp01(pRaw)

    // Phase A: solo hero image -> columns.
    const toColumns = smoothstep(HERO_CONFIG.soloToColumnsStart, HERO_CONFIG.soloToColumnsEnd, p)
    // Phase B: columns -> sphere.
    const toSphere = smoothstep(SPHERE_CONFIG.formStart, SPHERE_CONFIG.completeAt, p)

    const scrollT = p * Math.PI * 2

    // Rotate the entire formation slightly (mostly once sphere starts).
    const g = groupRef.current
    if (g) {
      const rotY = (scrollT * 0.35 + Math.sin(t * 0.25) * 0.18) * toSphere
      const rotX = (Math.cos(t * 0.22) * 0.12) * toSphere
      g.rotation.y = MathUtils.damp(g.rotation.y, rotY, 4.5, delta)
      g.rotation.x = MathUtils.damp(g.rotation.x, rotX, 4.5, delta)

      // Move the sphere away from the camera without breaking centering.
      g.position.z = MathUtils.damp(g.position.z, SPHERE_CENTER_Z * toSphere, 4.5, delta)
    }

    for (let i = 0; i < items.length; i += 1) {
      const m = meshesRef.current[i]
      if (!m) continue

      const it = items[i]
      const localT = t * (0.55 + it.drift * 0.22) + it.phase

      // Solo: only the first tile is visible & large.
      const isHero = i === 0
      const soloX = isHero ? 0 : (it.seed - 0.5) * 0.6
      const soloY = isHero ? 0 : (it.seed2 - 0.5) * 0.6
      const soloZ = isHero ? -1.35 : (-2.6 - it.seed3 * 1.2)

      // Columns layout (line of columns).
      const cx = it.colX
      const cy = it.colY
      const cz = it.colZ

      // Sphere layout.
      const sx = it.sphereX
      const sy = it.sphereY
      const sz = it.sphereZ

      // Cohesive "school" motion that responds to scroll (not just time).
      const flowT = localT * 0.9 + scrollT * 0.85
      const fieldX = Math.sin(flowT + cx * 0.18) + Math.cos(flowT * 0.6 + cy * 0.22)
      const fieldY = Math.cos(flowT * 0.82 + cy * 0.2) + Math.sin(flowT * 0.55 + cx * 0.15)
      const fieldZ = Math.sin(flowT * 0.5 + (cx + cy) * 0.1)

      const swimAmp = (it.w * 0.55) * (0.9 + it.seed * 0.6)
      const swimX = fieldX * swimAmp * 0.24
      const swimY = fieldY * swimAmp * 0.2
      const swimZ = fieldZ * 0.45

      // Motion is strongest in the columns stage, then settles as the sphere forms.
      const swimStrength = smoothstep(0.16, 0.7, p) * (1 - toSphere)

      const columnsX = cx + swimX * swimStrength
      const columnsY = cy + swimY * swimStrength
      const columnsZ = cz + swimZ * swimStrength

      // Blend: solo -> columns -> sphere.
      const preX = MathUtils.lerp(soloX, columnsX, toColumns)
      const preY = MathUtils.lerp(soloY, columnsY, toColumns)
      const preZ = MathUtils.lerp(soloZ, columnsZ, toColumns)

      const targetX = MathUtils.lerp(preX, sx, toSphere)
      const targetY = MathUtils.lerp(preY, sy, toSphere)
      const targetZ = MathUtils.lerp(preZ, sz, toSphere)

      m.position.x = MathUtils.damp(m.position.x, targetX, 5.0, delta)
      m.position.y = MathUtils.damp(m.position.y, targetY, 5.0, delta)
      m.position.z = MathUtils.damp(m.position.z, targetZ, 5.0, delta)

      const rotZ = Math.sin(localT * 0.7) * 0.1 * swimStrength
      const rotX = Math.cos(localT * 0.62) * 0.07 * swimStrength

      // Proper sphere-facing orientation:
      // - build a "swim" quaternion for the mid-phase motion
      // - slerp into the precomputed sphere quaternion
      // - then damp towards the result for smoothness
      const swimQ = new Quaternion().setFromEuler(new Euler(rotX, 0, rotZ, 'XYZ'))
      const sphereQ = new Quaternion(it.sphereQx, it.sphereQy, it.sphereQz, it.sphereQw)
      const targetQ = swimQ.slerp(sphereQ, toSphere)
      const qT = 1 - Math.exp(-6.5 * delta)
      m.quaternion.slerp(targetQ, qT)

      // Per-tile reveal: after entering section, tiles gradually appear.
      const appearAt = 0.16 + it.seed * 0.42
      const appear = isHero ? 1 : smoothstep(appearAt, appearAt + 0.14, p)
      const opacityTarget = MathUtils.lerp(0, 0.92, appear)

      const mat = m.material as MeshBasicMaterial
      mat.opacity = MathUtils.damp(mat.opacity, opacityTarget, 10.0, delta)

      if (isHero && heroAnchorRef) {
        // Track the right edge of the hero tile (so the label can sit to its right).
        heroRightWorldPos.set(it.w / 2, 0, 0)
        m.localToWorld(heroRightWorldPos)
        heroRightWorldPos.project(state.camera)
        heroAnchorRef.current.x = clamp01((heroRightWorldPos.x + 1) / 2)
        heroAnchorRef.current.y = clamp01((1 - heroRightWorldPos.y) / 2)
      }

      // Scale choreography: hero starts large, then relaxes; sphere ends tighter.
      const soloScale = isHero ? 4.25 : 0.12
      const columnsScale = 1.0
      // Slight depth cue: front tiles a bit larger, back tiles a bit smaller.
      const sphereScale = 0.78 * (0.86 + it.sphereDepth01 * 0.28)
      const s0 = MathUtils.lerp(soloScale, columnsScale, toColumns)
      const s1 = MathUtils.lerp(s0, sphereScale, toSphere)
      const sFinal = isHero ? s1 : s1 * MathUtils.lerp(0.01, 1.0, appear)
      m.scale.setScalar(MathUtils.damp(m.scale.x, sFinal, 7.5, delta))
    }
  })

  return (
    <group ref={groupRef}>
      {items.map((it, i) => {
        const tex = textures[i % textures.length]
        return (
          <mesh
            // eslint-disable-next-line react/no-array-index-key
            key={`${it.url}-${i}`}
            ref={(el) => {
              meshesRef.current[i] = el
            }}
            position={[0, 0, -2]}
          >
            <planeGeometry args={[it.w, it.h]} />
            <meshBasicMaterial
              map={tex}
              transparent
              opacity={i === 0 ? 0.92 : 0}
              side={DoubleSide}
              toneMapped={false}
            />
          </mesh>
        )
      })}
    </group>
  )
}

function clamp01(v: number): number {
  if (v < 0) return 0
  if (v > 1) return 1
  return v
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0))
  return t * t * (3 - 2 * t)
}

function hashTo01(input: string): number {
  // Small deterministic hash: string -> [0, 1)
  let h = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }

  // >>> 0 makes it unsigned 32-bit
  return ((h >>> 0) % 10000) / 10000
}

function centeredUnitSpherePoints(
  pts: Array<{ x: number; y: number; z: number }>,
): Array<{ x: number; y: number; z: number }> {
  if (pts.length <= 1) return pts

  // Finite Fibonacci sets can have a small centroid bias; subtract it so the
  // final sphere is visually centered, then renormalize to unit radius.
  let cx = 0
  let cy = 0
  let cz = 0
  for (const p of pts) {
    cx += p.x
    cy += p.y
    cz += p.z
  }
  cx /= pts.length
  cy /= pts.length
  cz /= pts.length

  return pts.map((p) => {
    const x = p.x - cx
    const y = p.y - cy
    const z = p.z - cz
    const len = Math.max(1e-6, Math.sqrt(x * x + y * y + z * z))
    return { x: x / len, y: y / len, z: z / len }
  })
}
